import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Users, FileText, CalendarCheck, TrendingUp,
  MoreHorizontal, ArrowRight, UserPlus, Download,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-orange-500',
];

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function formatDate(d) {
  if (!d) return 'Never';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return 'Unknown'; }
}

export default function AdminAgents({ onAssignTask }) {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/agents`, getAuthHeaders());
        setAgents(res.data.agents || []);
      } catch (e) {
        console.error('Load agents error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>;

  const activeCount = agents.filter(a => a.status === 'active').length;
  const totalLeads = agents.reduce((s, a) => s + a.total_leads, 0);
  const avgLeads = agents.length > 0 ? (totalLeads / agents.length).toFixed(1) : 0;
  const avgConv = agents.length > 0 ? (agents.reduce((s, a) => s + a.conversion_rate, 0) / agents.length).toFixed(1) : 0;
  const maxLeads = Math.max(...agents.map(a => a.total_leads), 1);

  return (
    <div className="space-y-6" data-testid="admin-agents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#111]">Agents</h1>
          <p className="text-sm text-slate-500">Manage your sales team and monitor performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs gap-1.5" data-testid="agents-export-csv">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
          <Button size="sm" className="text-xs gap-1.5 bg-[#111] hover:bg-[#333]" onClick={() => navigate('/users')} data-testid="agents-invite-btn">
            <UserPlus className="w-3.5 h-3.5" /> Invite agent
          </Button>
        </div>
      </div>

      {/* Stat Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total agents', value: agents.length, icon: Users },
          { label: 'Active agents', value: activeCount, icon: Users },
          { label: 'Avg leads/agent', value: avgLeads, icon: FileText },
          { label: 'Avg conversion', value: `${avgConv}%`, icon: TrendingUp },
        ].map((s) => (
          <Card key={s.label} className="border border-[#ebebeb] shadow-none rounded-[10px]">
            <CardContent className="px-4 py-3">
              <p className="text-[10px] text-slate-400 font-medium">{s.label}</p>
              <p className="text-lg font-bold text-[#111] mt-0.5">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Agent Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="agents-grid">
        {agents.map((agent, idx) => (
          <Card key={agent.id} className="border border-[#ebebeb] shadow-none rounded-xl hover:shadow-md transition-all" data-testid={`agent-card-${agent.id}`}>
            <CardContent className="p-5">
              {/* Top */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}>
                    {getInitials(agent.name)}
                  </div>
                  <div>
                    <p className="text-[15px] font-semibold text-[#111] leading-tight">{agent.name}</p>
                    <p className="text-[11px] text-slate-400">{agent.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className={`text-[9px] py-0 border-0 ${
                    agent.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    agent.status === 'unverified' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {agent.status === 'active' ? 'Active' : agent.status === 'unverified' ? 'Unverified' : 'Inactive'}
                  </Badge>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[
                  { label: 'This mo.', value: agent.month_leads },
                  { label: 'Total', value: agent.total_leads },
                  { label: 'Demos', value: agent.demos },
                  { label: 'Conv.', value: `${agent.conversion_rate}%` },
                ].map((s) => (
                  <div key={s.label} className="text-center bg-slate-50 rounded-lg px-2 py-2">
                    <p className="text-[10px] text-slate-400">{s.label}</p>
                    <p className="text-sm font-bold text-[#111]">{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <Progress value={Math.max((agent.total_leads / maxLeads) * 100, 2)} className="h-1.5" />
              </div>

              <p className="text-[10px] text-slate-400 mb-3">Last active: {formatDate(agent.last_active)}</p>

              {/* Footer */}
              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                <button onClick={() => navigate('/leads')} className="text-[11px] font-medium text-[#0071E3] hover:underline">View leads <ArrowRight className="w-3 h-3 inline" /></button>
                <span className="text-slate-200">|</span>
                <button onClick={() => onAssignTask && onAssignTask(agent)} className="text-[11px] font-medium text-[#0071E3] hover:underline" data-testid={`assign-task-${agent.id}`}>
                  Assign task <ArrowRight className="w-3 h-3 inline" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
